import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger
} from '@klear/ui/components/dialog'
import { Button, type ButtonProps } from '@klear/ui/components/button'
import { FormProvider } from 'react-hook-form'
import type { FieldValues, UseFormReturn } from 'react-hook-form'
import { Alert, AlertTitle, AlertDescription } from '@klear/ui/components/alert'
import { cn } from '@klear/ui/lib/utils'
import noop from 'lodash/noop'

interface ModalProps {
  title?: string
  description?: string
  children: React.ReactNode
  footer?: React.ReactNode
  form?: UseFormReturn<FieldValues>
  className?: string
  footerClassName?: string
  onSubmit?: (data: FieldValues) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  errorList?: string[]
  size?: 'sm' | 'md' | 'lg'
  primaryText?: string
  handlePrimaryAction?: () => void
  primaryDisabled?: boolean
  secondaryText?: string
  handleSecondaryAction?: () => void
  secondaryDisabled?: boolean
  modalButtonTitle?: string
  modalButtonIcon?: React.ReactNode
  modalButtonDisabled?: boolean
  buttonProps?: ButtonProps
  hideCloseButton?: boolean
  disableOutsideClick?: boolean
  disableEscapeKey?: boolean
  hideOverlay?: boolean
}

const Modal = ({
  title,
  description,
  children,
  footer,
  form,
  className,
  footerClassName,
  open,
  onSubmit = noop,
  primaryText,
  handlePrimaryAction,
  primaryDisabled,
  secondaryText,
  handleSecondaryAction,
  onOpenChange,
  errorList = [],
  size = 'md',
  modalButtonTitle,
  modalButtonIcon,
  modalButtonDisabled,
  buttonProps,
  hideCloseButton = false,
  disableOutsideClick = false,
  disableEscapeKey = false,
  hideOverlay = false
}: ModalProps) => {
  const content = (
    <div className="flex flex-col h-full flex-1 min-h-0">
      <div className="flex flex-col flex-1 min-h-0">
        {(title || description) && (
          <DialogHeader className="flex-shrink-0">
            <div className="p-4 flex flex-col gap-1">
              {title && <DialogTitle>{title}</DialogTitle>}
              {description && <DialogDescription className="">{description}</DialogDescription>}
            </div>
          </DialogHeader>
        )}
        {errorList.length > 0 && (
          <Alert variant="destructive" className="mb-4 flex-shrink-0 max-h-[20%] overflow-y-auto">
            <AlertTitle>There are errors in your submission</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5">
                {errorList.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex-1 overflow-y-auto px-4 space-y-8 min-h-0 pb-8">{children}</div>
      </div>
      {footer && (!primaryText || !secondaryText) && (
        <DialogFooter className={cn('border-t px-4 pt-4 pb-4 bg-background flex-shrink-0', footerClassName)}>
          {footer}
        </DialogFooter>
      )}
      {primaryText && (
        <DialogFooter className={cn('border-t px-4 pt-4 pb-4 bg-background flex-shrink-0', footerClassName)}>
          <>
            {secondaryText && handleSecondaryAction && (
              <Button type="button" variant="outline" onClick={handleSecondaryAction}>
                {secondaryText}
              </Button>
            )}
            {primaryText && form && (
              <Button type="submit" variant="default" disabled={primaryDisabled}>
                {primaryText}
              </Button>
            )}
            {primaryText && !form && handlePrimaryAction && (
              <Button type="button" variant="default" disabled={primaryDisabled} onClick={handlePrimaryAction}>
                {primaryText}
              </Button>
            )}
          </>
        </DialogFooter>
      )}
    </div>
  )

  const sizeClass = {
    sm: 'sm:max-w-[600px] sm:max-h-[min(80vh,1000px)] max-h-[100vh] p-0',
    md: 'max-w-4xl sm:max-h-[90vh] max-h-[95vh] mx-auto min-h-fit',
    lg: 'sm:max-w-[800px] max-h-[95vh]'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {modalButtonTitle && (
        <DialogTrigger asChild>
          <Button variant="outline" disabled={modalButtonDisabled} {...buttonProps}>
            {modalButtonIcon ? modalButtonIcon : null}
            {modalButtonTitle}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        className={cn(sizeClass[size], 'h-full mx-auto my-auto', className)}
        hideCloseButton={hideCloseButton}
        disableOutsideClick={disableOutsideClick}
        disableEscapeKey={disableEscapeKey}
        hideOverlay={hideOverlay}
      >
        {form ? (
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="h-full flex flex-col min-h-0 overflow-hidden">
              {content}
            </form>
          </FormProvider>
        ) : (
          content
        )}
      </DialogContent>
    </Dialog>
  )
}

export { Modal }
